import React, { Component, PureComponent } from 'react';
import ReactDOM from 'react-dom';
import SweetAlert from 'sweetalert-react';
import 'sweetalert-react/node_modules/sweetalert/dist/sweetalert.css';
import { Editor } from '@tinymce/tinymce-react';
import throttle from 'lodash.throttle';
import RoundButton from "components/RoundButton/roundbutton";
import EmbedModal from "./blockquote-modal.jsx";

import { showPiscoModal } from "../new-story-actions.js";
import { logArticleImageSource } from "../../media-picker/image-source-log.js";
import { PiscoContentTypes } from "app-constants/index.js";
import { getCountFromContent } from "utils/word-counts";
import $ from "jquery";

export default class TinyMCEEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      fullScreenState: false,
      showImageFlipperModal: false,
      showBlockQuoteModal: false,
      alertDetails: {
        showCancleButton: false,
        showAlert: false,
        title: "",
        text: ""
      },
      blockQuote: 0,
    };

    // Caret handling
    this._bookmark = null;
    // Last non-collapsed text selection, kept so the hyperlink dialog can read the
    // highlighted text into "Text to display" even if focus/re-render collapses it.
    this._linkBookmark = null;

    this.handleEditorChange = throttle(this.handleEditorChange.bind(this), 5 * 1000);
    this.imagesUploadHandler = this.imagesUploadHandler.bind(this); // kept for compatibility (unused in init)
    this.appendBlockQuoteIncontentArea = this.appendBlockQuoteIncontentArea.bind(this);
    this.appendImageFlipperInContentArea = this.appendImageFlipperInContentArea.bind(this);
    this.onEditorContentChange = this.onEditorContentChange.bind(this);
    this._appendIamgeFlipper = this._appendIamgeFlipper.bind(this);
    this.hideAlert = this.hideAlert.bind(this);
    this.filePickerCallback = this.filePickerCallback.bind(this); // kept for compatibility (unused in init)
    this.getYouTubeId = this.getYouTubeId.bind(this);
    this.fetchYTMeta = this.fetchYTMeta.bind(this);

    // caret helpers
    this._storeCaret = this._storeCaret.bind(this);
    this._restoreCaret = this._restoreCaret.bind(this);
    this._insertWithCaretAfter = this._insertWithCaretAfter.bind(this);

    // cleanup helpers
    this._cleanEmptyFigures = this._cleanEmptyFigures.bind(this);

    // small helpers
    this._escapeAttr = this._escapeAttr.bind(this);
    this._buildYouTubeEmbedUrl = this._buildYouTubeEmbedUrl.bind(this);

    // Inline body quick-add (article-local upload, NOT catalogued)
    this._quickAddInput = null;
    this._doQuickAdd = this._doQuickAdd.bind(this);
  }

  // ----- helpers -----
  _escapeAttr(v) {
    return String(v == null ? "" : v)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  // Plain embed – no JS API and no origin parameter (avoids the “An error occurred” pane)
  _buildYouTubeEmbedUrl(videoId) {
    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`;
  }
  // -------------------

  // ----- Caret helpers -----
  _storeCaret(editor) {
    try {
      // Type 3 = ID-based bookmark that survives async/DOM changes
      this._bookmark = editor.selection.getBookmark(3, true);
    } catch (e) {
      this._bookmark = null;
    }
  }

  _restoreCaret(editor) {
    try {
      editor.focus();
      if (this._bookmark) {
        editor.selection.moveToBookmark(this._bookmark);
      } else {
        var body = editor.getBody();
        if (body) {
          editor.selection.select(body, true);
          editor.selection.collapse(false);
        }
      }
    } catch (e) { /* ignore */ }
  }

  _insertWithCaretAfter(editor, html) {
    // Insert content followed by a temporary marker; move caret after it and keep it in view
    var markerId = 'mce_caret_' + Date.now() + '_' + Math.floor(Math.random() * 100000);
    var htmlWithMarker = html + '<span id="' + markerId + '">\u200B</span>';

    editor.execCommand('mceInsertContent', false, htmlWithMarker);
    editor.focus();

    var markerElm = editor.dom.get(markerId);
    if (markerElm) {
      editor.selection.select(markerElm);
      editor.selection.collapse(false);
      if (markerElm.scrollIntoView) {
        markerElm.scrollIntoView({ block: 'nearest' });
      }
      editor.dom.remove(markerElm);
    }
  }

  // Inline body quick-add: upload an image straight to the article's own S3
  // bucket (absinthe POST /article/:domainId/asset/:articleId) and insert it.
  // Deliberately does NOT write to the Media Library or pisco catalog — this is
  // an article-local, non-catalogued image. Bypasses setPiscoContent so it can't
  // collide with the pisco componentDidUpdate insert path.
  _doQuickAdd(file) {
    var self = this;
    var editor = this.props.tinyMCEEditor.current && this.props.tinyMCEEditor.current.editor;
    if (!editor || !file) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert("Image too big — max 5MB for inline upload.");
      return;
    }

    var caption = (window.prompt("Caption (optional):", "") || "").trim();
    var credit = (window.prompt("Credit / photographer (optional):", "") || "").trim();

    var articleId = this.props.articleId;
    var domainId = this.props.activeDomain;
    var token = localStorage.getItem("accessToken");
    var base = process.env.REACT_APP_API_SERVER || "";

    var fd = new FormData();
    fd.append("asset", file); // absinthe multer field name is 'asset'

    // path includes '/article'; response body is { result: "<url>" }
    var url = base + "/article/" + domainId + "/asset/" + articleId;
    fetch(url, { method: "POST", headers: { authorization: token }, body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error("quick-add upload failed: " + r.status);
        return r.json();
      })
      .then(function (json) {
        var src = json && json.result;
        if (!src) throw new Error("quick-add: no url in response");

        var capHtml = caption;
        if (credit && caption.indexOf(credit) === -1) {
          capHtml = caption ? (caption + " — " + self._escapeAttr(credit)) : self._escapeAttr(credit);
        }

        var figure =
          '<figure class="image" contenteditable="false" data-mce-selected="1">' +
            '<img id="figure-image" contenteditable="false" src="' + self._escapeAttr(src) + '" alt="' + self._escapeAttr(caption) + '" />' +
            '<figcaption contenteditable="true" data-gramm="false">' + capHtml + '</figcaption>' +
          '</figure><p></p>';

        self._restoreCaret(editor);
        self._insertWithCaretAfter(editor, figure);
        self._bookmark = null;

        logArticleImageSource({
          articleId: articleId,
          domainId: domainId,
          placement: "body",
          source: "quick_add",
          assetUrl: src
        });
      })
      .catch(function (err) {
        console.error("quick-add failed", err);
        window.alert("Upload failed — please try again.");
      });
  }

  _cleanEmptyFigures(editor) {
    var body = editor.getBody();
    if (!body) return;
    body.querySelectorAll("figure").forEach(function (fig) {
      if (!fig.querySelector("img")) {
        fig.parentNode.removeChild(fig);
      }
    });
  }

  _storeLinkBookmark(editor) {
    // Snapshot the live non-collapsed selection so the hyperlink button can restore it
    // if focus/re-render collapses the highlight before the link dialog reads it.
    if (!editor.selection.isCollapsed()) {
      try {
        this._linkBookmark = editor.selection.getBookmark(2);
      } catch (e) { /* ignore */ }
    }
  }
  // --------------------------

  handleEditorChange(content) {
    const updateApi = this.props.updateApi;
    const domain = this.props.domain;
    const featurePermision = domain && domain.featurePermision;

    if (featurePermision && featurePermision.indexOf('LAZYLOAD_ARTICLES') > -1) {
      content = content.replace(
        /<img\s+(?:id="([^"]*)"\s+)?(?:contenteditable="([^"]*)"\s+)?(?:data-src="((?!["\s])[^"]{1,})"\s+)(?:alt="([^"]*)"\s+)?(?:data-mce-nsrc="([^"]*)"\s+)?(?:data-mce-src="([^"]*)"\s+)?[^>]*>/g,
        function (match, id, contenteditable, src, alt, dataMceNsrc, dataMceSrc) {
          return '<img id="figure-image" class="lazyload" contenteditable="false" data-src=' + src + ' alt="' + (alt ? alt.trim() : '') + '" data-mce-nsrc="' + dataMceNsrc + '" />';
        }
      );
    }
    updateApi && updateApi(content);
  }

  componentDidMount() {
    const setInitialState = this.props.setInitialState;
    setInitialState && setInitialState();

    // Force TinyMCE UI above any fullscreen overlays
    if (!document.getElementById('tinymce-zfix')) {
      const style = document.createElement('style');
      style.id = 'tinymce-zfix';
      style.textContent = `
        .tox-tinymce-aux,
        .tox-dialog-wrap,
        .tox-silver-sink,
        .tox-pop,
        .tox-menu {
          z-index: 2147483647 !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activePiscoContentType === PiscoContentTypes.CONTENT) {
      var contentExists = !!this.props.piscoContent;
      var contentIsValid = this.props.piscoContent && Object.keys(this.props.piscoContent).length > 0;
      var contentIsNew = this.props.piscoContent && prevProps.piscoContent && (this.props.piscoContent.cdn !== prevProps.piscoContent.cdn);

      if (contentExists && contentIsValid && contentIsNew) {
        var editor = this.props.tinyMCEEditor.current && this.props.tinyMCEEditor.current.editor;
        if (!editor) return;

        // Remove orphaned <figure> shells (no <img>) so new image can't nest inside them
        this._cleanEmptyFigures(editor);

        // Restore caret where user triggered Pisco
        this._restoreCaret(editor);

        // Compose the figcaption as caption + an (HTML-escaped) credit line, so
        // attribution from the Pisco Credit/Photographer field is carried into
        // the article body instead of being dropped. Idempotent; only the credit
        // is escaped (it becomes an untrusted value once auto-populated).
        var _rawCaption = this.props.piscoContent.caption || '';
        var _credit = ((this.props.piscoContent.credit || this.props.piscoContent.photographer) || '').toString().trim();
        var _escapeHtml = function (s) {
          return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
        };
        var imageCaptionHtml = _rawCaption;
        if (_credit && _rawCaption.indexOf(_credit) === -1) {
          imageCaptionHtml = _rawCaption ? (_rawCaption + ' — ' + _escapeHtml(_credit)) : _escapeHtml(_credit);
        }

        var content = '';
        switch (this.props.piscoContent.type) {
          case "Image": {
            if (this.props.piscoContent.comp_base_str || this.props.piscoContent.base_64) {
              content = '\
                <figure class="image" contenteditable="false" data-mce-selected="1">\
                  <img id="figure-image" contenteditable="false" src="' + this.props.piscoContent.cdn + '"  alt="' + this.props.piscoContent.caption + '" data-mce-nsrc="data:image/webp;base64, ' + (this.props.piscoContent.comp_base_str || this.props.piscoContent.base_64) + '" />\
                  <figcaption contenteditable="true" data-gramm="false">' + imageCaptionHtml + '</figcaption>\
                </figure><p></p>';
            } else {
              content = '\
                <figure class="image" contenteditable="false" data-mce-selected="1">\
                  <img id="figure-image" contenteditable="false" src="' + this.props.piscoContent.cdn + '"  alt="' + this.props.piscoContent.caption + '" />\
                  <figcaption contenteditable="true" data-gramm="false">' + imageCaptionHtml + '</figcaption>\
                </figure><p></p>';
            }
            break;
          }
          case "Video": {
            if (this.props.piscoContent.cloudinary) {
              var cld = this.props.piscoContent.cloudinary;
              // Videos uploaded before the Cloudinary account switch still live
              // in the old cloud. Their stored upload response carries the cloud
              // name in secure_url, so play each asset from the cloud it is
              // actually in; new uploads fall through to the current account.
              var storedCloud = (String(cld.secure_url || cld.url || "").match(/res\.cloudinary\.com\/([^\/]+)\//) || [])[1];
              var cloudName = storedCloud || process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || "edofvma7";
              var publicId = cld.public_id;
              content = '\
                <div class="blockquote-container" contenteditable="false">\
                  <iframe class="cloudinaryVideoEncoded" src="https://player.cloudinary.com/embed/?cloud_name=' + cloudName + '&public_id=' + publicId + '&profile=cld-default" width="' + this.props.piscoContent.cloudinary.width + '" height="' + this.props.piscoContent.cloudinary.height + '" style="height: auto; width: 100%;" allow="autoplay; fullscreen; encrypted-media; picture-in-picture" allowfullscreen frameborder="0"></iframe>\
                  <p class="end-of-block-quote">&#8203;</p>\
                </div>';
            } else {
              content = '\
                <div class="blockquote-container" contenteditable="false">\
                  <iframe width="560" height="315" frameborder="0" align="middle" src="https://players.brightcove.net/60156980900011/gUY2vwlJSg_default/index.html?videoId=' + this.props.piscoContent.brightcove_video_id + '" allowfullscreen webkitallowfullscreen mozallowfullscreen></iframe>\
                  <p class="end-of-block-quote"></p>\
                </div>';
            }
            break;
          }
          default:
            break;
        }

        // Insert content and keep caret after it
        this._insertWithCaretAfter(editor, content);

        this._bookmark = null;
        this.props.hidePiscoModal && this.props.hidePiscoModal();
        this.props.clearPiscoContent && this.props.clearPiscoContent();
      }
    }
  }

  componentWillUnmount() {
    this.handleEditorChange.flush();
    this.handleEditorChange.cancel();
  }

  // UPDATED: robust extraction from URLs or pasted iframes (watch, youtu.be, embed, shorts, live)
  getYouTubeId(input) {
    if (!input) return null;
    const str = String(input);

    // If an <iframe> was pasted, extract its src first
    const srcMatch = str.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    const url = srcMatch ? srcMatch[1] : str;

    const m =
      url.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts\/|embed\/|v\/|watch\?v=|live\/))([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
  }

  fetchYTMeta(videoId) {
    return new Promise(function (resolve, reject) {
      var KEY = 'AIzaSyA6ihLtYuzs_AUL5wTvzsVr1ePkf3DAItM';
      if (!KEY) return reject(new Error('Missing YT_API_KEY'));

      var qs = '?part=snippet,contentDetails,player&id=' + videoId + '&key=' + KEY;

      fetch('https://www.googleapis.com/youtube/v3/videos' + qs)
        .then(function (res) {
          if (!res.ok) throw new Error('YT API ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var items = data.items;
          if (!items || !items.length) throw new Error('Video not found');

          var snippet = items[0].snippet;
          var contentDetails = items[0].contentDetails;
          var player = items[0].player;

          var thumbnails = snippet.thumbnails || {};
          var thumb =
            (thumbnails.maxres && thumbnails.maxres.url) ||
            (thumbnails.standard && thumbnails.standard.url) ||
            (thumbnails.high && thumbnails.high.url) ||
            (thumbnails.medium && thumbnails.medium.url) ||
            (thumbnails.default && thumbnails.default.url);

          var embedHtml = (player && player.embedHtml) || '';
          var mp4Match = embedHtml.match(/src="([^"]+\.mp4[^"]*)"/);

          resolve({
            title: snippet.title,
            description: snippet.description,
            thumb: thumb,
            duration: contentDetails.duration,
            mp4: mp4Match ? mp4Match[1] : undefined
          });
        })
        .catch(function (err) {
          console.log(">>>>>>> error", err);
          reject(err);
        });
    });
  }

  async appendBlockQuoteIncontentArea(content) {
    var editor = this.props.tinyMCEEditor.current && this.props.tinyMCEEditor.current.editor;
    if (!editor) return;

    // Prefer bookmark captured when modal opened. If absent, capture now.
    if (!this._bookmark) {
      try { this._bookmark = editor.selection.getBookmark(3, true); } catch (e) { this._bookmark = null; }
    }

    // ===== YOUTUBE EMBED (fixed) =====
    const ytId = this.getYouTubeId(content);
    if (ytId) {
      try {
        let meta = { title: 'YouTube video player', thumb: '', duration: '', mp4: undefined };
        try {
          // Best effort: enrich with meta (non-blocking for the embed)
          meta = await this.fetchYTMeta(ytId);
        } catch (ignore) { /* embed should still work without meta */ }

        // Restore caret to where user triggered the embed
        editor.focus();
        if (this._bookmark) {
          editor.selection.moveToBookmark(this._bookmark);
        }

        const iframe =
          '<iframe' +
          ' class="youtube-embed"' +
          ' width="560" height="315"' +
          ' src="' + this._buildYouTubeEmbedUrl(ytId) + '"' +
          ' title="' + this._escapeAttr(meta.title || 'YouTube video player') + '"' +
          ' frameborder="0"' +
          ' allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share"' +
          ' allowfullscreen' +
          (meta.thumb ? ' data-thumb="' + this._escapeAttr(meta.thumb) + '"' : '') +
          (meta.duration ? ' data-duration="' + this._escapeAttr(meta.duration) + '"' : '') +
          (meta.mp4 ? ' data-mp4="' + this._escapeAttr(meta.mp4) + '"' : '') +
          '></iframe>';

        this._insertWithCaretAfter(editor, iframe);

        this._bookmark = null;
        return;
      } catch (e) {
        alert('Some error inform technical team');
        console.log(">>>>>> youtube embed error", e);
        return;
      }
    }
    // ===== END YOUTUBE EMBED =====

    // ADTHRIVE EMBED
    if (content.indexOf('adthrive-video-player') > -1) {
      editor.focus();
      if (this._bookmark) {
        editor.selection.moveToBookmark(this._bookmark);
      }

      const temp = document.createElement('div');
      temp.innerHTML = content;
      const oldEmbed = temp.querySelector('div.adthrive-video-player');
      if (!oldEmbed) return;

      const ds = oldEmbed.dataset || {};
      const videoId = ds.videoId || '';
      const playerType = ds.playerType || 'default';
      const override = oldEmbed.getAttribute('override-embed') || 'default';

      const meta = function (prop) {
        const el = oldEmbed.querySelector('meta[itemprop="' + prop + '"]');
        return (el && el.getAttribute('content')) || '';
      };

      const uploadDate = meta('uploadDate');
      const name = meta('name');
      const description = meta('description');
      const thumbnailUrl = meta('thumbnailUrl');
      const contentUrl = meta('contentUrl');

      const articleId = this.props.articleId;
      const domain = this.props.activeDomain;
      const token = localStorage.getItem('accessToken');
      const htmlStr = oldEmbed.outerHTML;
      const videoSource = 'RAPTIVE';

      const url = (process.env.REACT_APP_API_SERVER || '') + '/article/' + domain + '/embed_video/' + articleId;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'authorization': token
          },
          body: JSON.stringify({
            htmlStr: htmlStr,
            videoSource: videoSource
          })
        });

        if (!response.ok) {
          throw new Error('HTTP error! status: ' + response.status);
        }

        const data = await response.json();
        console.log('Video embed response:', data);
      } catch (err) {
        console.error('Error during video embed fetch:', err);
      }

      const iframeHTML = '\
        <div>\
          <iframe\
            itemscope\
            itemtype="http://schema.org/VideoObject"\
            class="adthrive-video-player in-post adThriveEncoded"\
            data-video-id="' + videoId + '"\
            data-player-type="' + playerType + '"\
            override-embed="' + override + '"\
            data-upload-date="' + uploadDate + '"\
            title="' + name + '"\
            data-description="' + description + '"\
            data-thumbnail-url="' + thumbnailUrl + '"\
            src="' + contentUrl + '"\
            allowfullscreen\
            frameborder="0"\
          ></iframe>\
        </div>';

      this._insertWithCaretAfter(editor, iframeHTML);
      this._bookmark = null;
      return;
    }

    // GENERIC EMBED
    editor.focus();
    if (this._bookmark) {
      editor.selection.moveToBookmark(this._bookmark);
    }
    const embededContent = content.trim();
    const blockquoteText = "<div class='blockquote-container' contenteditable='false'>" + embededContent + "</div>";
    this._insertWithCaretAfter(editor, blockquoteText);
    this._bookmark = null;
  }

  _appendIamgeFlipper(content) {
    var editor = this.props.tinyMCEEditor.current && this.props.tinyMCEEditor.current.editor;
    if (!editor) return;
    this._restoreCaret(editor);
    this._insertWithCaretAfter(editor, (content || '').trim());
    this._bookmark = null;
  }

  appendImageFlipperInContentArea(data) {
    const uploadFlipperedImages = this.props.uploadFlipperedImages;
    uploadFlipperedImages && uploadFlipperedImages(data, this._appendIamgeFlipper);
  }

  hideAlert() {
    this.setState({
      alertDetails: {
        showAlert: false,
        title: "",
        text: ""
      }
    });
  }

  // kept only for backward compatibility; NOT used in TinyMCE init
  imagesUploadHandler(blobInfo, success, failure) {
    const tinyMCEEditorKlass = this;
    if (blobInfo.blob().size > 2097152) {
      tinyMCEEditorKlass.setState({
        alertDetails: {
          showCancleButton: false,
          showAlert: true,
          title: "Error!",
          text: String("Image Files too big, Max Size Allowed is 2MB")
        }
      });
      success();
      return;
    }
    success();
  }

  // kept only for backward compatibility; NOT used in TinyMCE init
  filePickerCallback(cb, value, meta) {
    // intentionally empty (we don't use TinyMCE image picking anymore)
  }

  onEditorContentChange(content) {
    this.handleEditorChange(content);
    this.props.updateEditorContent && this.props.updateEditorContent(content);
  }

  render() {
    const shareType = this.props.shareType;
    const editorContent = this.props.editorContent;
    const showBlockQuote = this.props.showBlockQuote;
    const hideBlockQuote = this.props.hideBlockQuote;
    const showBlockQuoteModal = this.props.showBlockQuoteModal;
    const setAppMessage = this.props.setAppMessage;
    const showPiscoModal = this.props.showPiscoModal;

    window.shareType = shareType;

    const showAlert = this.state.alertDetails.showAlert || false;
    const title = this.state.alertDetails.title || "";
    const text = this.state.alertDetails.text || "";

    const fullScreenState = this.state.fullScreenState;
    let containerClass = "tinymce-editor-container ".concat(
      fullScreenState ? " tinymce-editor-container-fullscreen" : ""
    );

    let editorDefaultContent = editorContent;
    if (this.props.tinyMCEEditor.current && !editorContent) {
      containerClass = containerClass.concat(" empty-container");
    }

    const totalWords = getCountFromContent(editorContent);

    const fullScreenIcon = fullScreenState ? (
      <RoundButton
        type='fullscreen_exit'
        className="fullscreen"
        location={{
          float: "right",
          position: "absolute",
          right: "-1px",
          top: "-5px",
          cursor: "pointer",
          zIndex: "99998"
        }}
        buttonbackground="white"
        iconcolor="#2b2d42"
        onclick={() => this.setState({ fullScreenState: false })}
      />
    ) : (
      <RoundButton
        type='fullscreen'
        className="fullscreen"
        location={{
          border: "0",
          float: "right",
          position: "absolute",
          right: "-1px",
          bottom: "1px",
        }}
        buttonbackground="white"
        iconcolor="#2b2d42"
        onclick={() => this.setState({ fullScreenState: true })}
      />
    );

    return (
      <div id="article-contents">
        <div
          className={containerClass}
          style={{ border: "2px solid #dddbdb", borderRadius: "4px" }}
        >
          <span
            style={{
              position: "absolute",
              right: fullScreenState ? "43px" : "3px",
              top: "9px",
              color: "black",
              fontWeight: "bold"
            }}
          >
            {totalWords} words
          </span>
          {fullScreenIcon}

          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={(el) => { this._quickAddInput = el; }}
            onChange={(e) => {
              var f = e.target.files && e.target.files[0];
              e.target.value = null;
              if (f) this._doQuickAdd(f);
            }}
          />

          <Editor
            contenteditable="true"
            ref={this.props.tinyMCEEditor}
            apiKey="nnqf35khd0owkobaobxk84b0n5xf0w5uzcv4kaepewkyfk5u"
            inline
            value={editorDefaultContent}
            // Disable tinymce-react's auto "rollback": ~200ms after an edit it calls
            // editor.setContent(value), which (in inline mode, when the editor isn't
            // focused) collapses the selection. That wiped the highlight before the
            // link dialog could read it. We already push every change back via
            // updateEditorContent, so the rollback safety-net is redundant here.
            rollback={false}
            init={{
              browser_spellcheck: true,
              contextmenu: false,

              placeholder: "Start writing........",
              
              ui_mode: 'split',
              // Make TinyMCE dialogs/toolbars sit above your fullscreen container
              zindex: 2147483647,
              ui_container: 'body',

              // Quickbars only
              plugins: "paste autosave media link autoresize table quickbars lists",
              menubar: false,
              toolbar: false,

              // Quickbars config (H+ also in selection toolbar)
              // NOTE: use our custom "hyperlink" button instead of the built-in "link"
              // "pisco" = the "Pick from media" picker (search pisco + Media Library
              // + article-local Upload inside it). The standalone "quickadd" (Upload)
              // toolbar button was removed — uploading now lives inside the picker.
              quickbars_insert_toolbar: "h2 h3 blockquote embed pisco table",
              quickbars_selection_toolbar: "bold italic hyperlink underline | h2 h3 hplus",

              pagebreak_separator: "<pagebreak>",
              min_height: 800,
              height: 800,
              autosave_interval: "5s",
              media_live_embeds: true,

              // styles and formats
              style_formats: [
                { title: "Heading", classes: "split", block: "h2" },
                { title: "Heading 3", classes: "split3", block: "h3" },
                { title: "Blockquote", block: "blockquote", classes: "meaww-blockquote" }
              ],
              formats: {
                h2: { block: "h2", classes: "split" },
                h3: { block: "h3", classes: "split3" },
                blockquote: { block: "blockquote", classes: "meaww-blockquote" }
              },

              spellchecker_language: "en",
              autoresize_bottom_margin: 50,
              autoresize_max_height: 1000,
              autoresize_min_height: 500,
              autoresize_overflow_padding: 50,
              paste_as_text: true,
              paste_data_images: false,
              entity_encoding: "raw",
              branding: false,

              color_picker_callback: function (callback, value) { callback("#FF00FF"); },

              setup: (editor) => {
                var self = this;

                // H+ (Quick Link Heading)
                editor.ui.registry.addButton("hplus", {
                  text: "H+",
                  tooltip: "Add Quick Link Heading",
                  onAction: function () {
                    var content = editor.selection.getContent({ format: "text" }).trim();
                    if (!content) return;
                    var id = content.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "");
                    editor.insertContent('<h2 id="' + id + '" class="quickLinks">' + content + "</h2>");
                  }
                });

                // H2 toggle
                editor.ui.registry.addToggleButton("h2", {
                  text: "H2",
                  tooltip: "Heading 2",
                  onAction: function () { editor.formatter.toggle("h2"); },
                  onSetup: function (api) {
                    var handler = function (state) { api.setActive(state); };
                    editor.formatter.formatChanged("h2", handler);
                    return function () { editor.formatter.formatChanged("h2", handler); };
                  }
                });

                // H3 toggle
                editor.ui.registry.addToggleButton("h3", {
                  text: "H3",
                  tooltip: "Heading 3",
                  onAction: function () { editor.formatter.toggle("h3"); },
                  onSetup: function (api) {
                    var handler = function (state) { api.setActive(state); };
                    editor.formatter.formatChanged("h3", handler);
                    return function () { editor.formatter.formatChanged("h3", handler); };
                  }
                });

                // Blockquote toggle
                editor.ui.registry.addToggleButton("blockquote", {
                  icon: "blockquote",
                  tooltip: "Blockquote",
                  onAction: function () { editor.formatter.toggle("blockquote"); },
                  onSetup: function (api) {
                    var handler = function (state) { api.setActive(state); };
                    editor.formatter.formatChanged("blockquote", handler);
                    return function () { editor.formatter.formatChanged("blockquote", handler); };
                  }
                });

                // **Hyperlink button (forces full modal dialog)**
                editor.ui.registry.addButton("hyperlink", {
                  icon: "link",
                  tooltip: "Insert/edit link",
                  onAction: function () {
                    // Re-assert focus + the captured selection so the dialog reads the
                    // highlighted text into "Text to display" instead of coming up blank
                    // (which would let the raw URL overwrite the selected text).
                    editor.focus();
                    if (self._linkBookmark) {
                      try {
                        editor.selection.moveToBookmark(self._linkBookmark);
                      } catch (e) { /* ignore */ }
                    }
                    // Force the modal Link dialog instead of the QuickLink popover
                    editor.execCommand("mceLink", false, { dialog: true });
                  }
                });

                // Embed button: save caret then open modal
                editor.ui.registry.addButton("embed", {
                  text: "Embed",
                  tooltip: "Embed",
                  onAction: function () {
                    self._storeCaret(editor);
                    var fn = self.props.showBlockQuote;
                    if (fn) { fn(editor); }
                  }
                });

                // "Pick from media" button: save caret then open the picker
                // (search pisco + Media Library, with article-local Upload inside).
                editor.ui.registry.addButton("pisco", {
                  text: "Pick from media",
                  tooltip: "Pick from media",
                  onAction: function () {
                    self._storeCaret(editor);
                    var fn = self.props.showPiscoModal;
                    if (fn) { fn(editor); }
                  }
                });

                // Quick-add: inline article-local image upload (NOT saved to any
                // library catalog). Secondary to Pisco/Media. Body only, flagged.
                if (process.env.REACT_APP_QUICK_ADD === "true") {
                  editor.ui.registry.addButton("quickadd", {
                    icon: "upload",
                    text: "Upload",
                    tooltip: "Upload image (article only — not saved to Library)",
                    onAction: function () {
                      self._storeCaret(editor);
                      if (self._quickAddInput) {
                        self._quickAddInput.value = "";
                        self._quickAddInput.click();
                      }
                    }
                  });
                }

                // Paste cleanup
                editor.on("PastePreProcess", function (e) {
                  e.content = e.content
                    .replace(/<\/p><p>/g, "<br>")
                    .replace(/<p>/g, "")
                    .replace(/<\/p>/g, "");
                });

                // Link targets and figure editability
                // Snapshot the highlighted text so the hyperlink button can restore it if the
                // selection collapses before the link dialog reads it. Listen to BOTH events:
                // NodeChange skips same-paragraph selection changes (its isSameElementPath gate),
                // so SelectionChange covers keyboard re-selection within a block, while NodeChange
                // gives a settled capture after mouse/command-driven changes.
                editor.on("SelectionChange", function () {
                  self._storeLinkBookmark(editor);
                });

                editor.on("NodeChange", function (e) {
                  var el = e.element;
                  var parent = el && el.parentNode;

                  // Clean up orphaned figures (no <img> inside) left after image deletion
                  self._cleanEmptyFigures(editor);

                  // Refresh the link bookmark on settled (mouse/command) selection changes
                  self._storeLinkBookmark(editor);

                  if (el && el.nodeName === "A") {
                    var url = el.getAttribute("href") || "";
                    var domain = window.shareType ? window.shareType : "meaww";

                  } else if (el && el.nodeName === "FIGCAPTION" && parent && parent.nodeName === "FIGURE") {
                    parent.setAttribute("contenteditable", "true");
                  } else {
                    var body = editor.getBody();
                    if (body) {
                      body.querySelectorAll("figure").forEach(function (fig) {
                        fig.setAttribute("contenteditable", "true");
                      });
                    }
                  }
                });
              },

              // Link plugin: ensure URL, Title, Target fields exist in dialog
              link_context_toolbar: true,
              link_quicklink: false,
              link_title: true,
              link_assume_external_targets: true,

              // ---- Link dialog options (cover TinyMCE 5 & 6) ----
              // v6 keys:
              link_target_list: [
                { title: "Current window", value: "" },       // or "_self"
                { title: "New window", value: "_blank" },
              ],
              // v5-compatible aliases:
              target_list: [
                { title: "Current window", value: "" },
                { title: "New window", value: "_blank" },
              ],
              // allow choosing _blank (TinyMCE will add rel=noopener for safety)
              allow_unsafe_link_target: true,
              // Optional: preselect “New window”
              // link_default_target: "_blank",
              // ---------------------------------------------------
            }}
            onEditorChange={this.onEditorContentChange}
          />

          <EmbedModal
            showBlockQuoteModal={showBlockQuoteModal}
            showBlockQuote={showBlockQuote}
            hideBlockQuote={hideBlockQuote}
            appendBlockQuote={this.appendBlockQuoteIncontentArea}
            setAppMessage={setAppMessage}
          />
          <SweetAlert
            show={showAlert}
            title={title}
            text={text}
            onConfirm={this.hideAlert}
          />
        </div>
      </div>
    );
  }
}
